import React, { useState, useEffect } from "react";
import { useTheme, getT, glassCard, getBrandColors } from "./ThemeContext";
import {
  FiStar, FiCheckCircle, FiChevronDown, FiChevronUp, FiUser, FiInfo, FiSend,
  FiZap, FiTrendingUp, FiMessageCircle, FiCalendar, FiShield, FiSmile, FiAlertTriangle, FiHeart, FiFileText, FiTarget, FiEdit3
} from "react-icons/fi";
import { FaClipboardCheck } from "react-icons/fa";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";

const CoachEvaluation = () => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);

  const [categories, setCategories] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Form State
  const [selectedCoach, setSelectedCoach] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [scores, setScores] = useState({}); // { metric_id: score }
  const [comments, setComments] = useState("");
  const [recommendation, setRecommendation] = useState(true);

  useEffect(() => {
    fetchConfig();
    fetchCoaches();
    fetchStudents();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/evaluations/config`);
      const data = await res.json();
      
      if (res.ok && Array.isArray(data)) {
        setCategories(data);
      } else {
        setCategories([]);
        setError("Invalid configuration format received.");
      }
    } catch (e) {
      setCategories([]);
      setError("Failed to load evaluation configuration.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      // Use trailing slash for reliability
      const res = await fetch(`${API_BASE}/users/`);
      const data = await res.json();
      const rawUsers = Array.isArray(data) ? data : (data.users || []);
      
      const coachesFiltered = rawUsers.filter(u => {
        const roleStr = (u.user_role || u.role || "").toString().toLowerCase();
        const active = (u.active_status !== 0 && u.is_active !== 0 && u.active_status !== "0");
        return roleStr === "coach" && active;
      }).map(u => ({
        id: u.user_id || u.id,
        name: u.full_name || u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username
      }));
      
      setCoaches(coachesFiltered);
    } catch (e) { console.error("Coach fetch failed", e); }
  };

  const fetchStudents = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("sixers_user") || "{}");
      const role = (user.user_role || user.role || "").toString().toLowerCase();
      const userId = user.user_id || user.parent_id || user.id;

      let url = `${API_BASE}/students/`;
      if (role === "parent" && userId) {
        // Correct endpoint from parent_accounts.py: GET /parent-accounts/{parent_id}/students
        url = `${API_BASE}/parent-accounts/${userId}/students`;
      }

      const res = await fetch(url);
      const data = await res.json();
      const rawStudents = Array.isArray(data) ? data : (data.students || []);
      
      const studentsFiltered = rawStudents.filter(s => {
        const st = (s.status || s.active_status || "Active").toString().toLowerCase();
        const deleted = (st === "inactive" || st === "0" || st === "dropped" || s.active_status === 0 || s.active_status === "0");
        return !deleted;
      }).map(s => ({
        id: s.student_id || s.id,
        name: s.name || `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.student_code || `Student #${s.student_id || s.id}`
      }));
      
      setStudents(studentsFiltered);
      if (studentsFiltered.length === 1) setSelectedStudent(studentsFiltered[0].id);
    } catch (e) { console.error("Student fetch failed", e); }
  };

  const handleScoreChange = (metricId, score) => {
    setScores(prev => ({ ...prev, [metricId]: score }));
  };

  // Scoring Logic (Live)
  const calculateStats = () => {
    if (!Array.isArray(categories) || categories.length === 0) return (0).toFixed(1);
    
    let totalWeighted = 0;
    let weightSum = 0;
    
    categories.forEach(cat => {
      const catMetrics = cat.metrics;
      const catScores = catMetrics.map(m => scores[m.id]).filter(s => s !== undefined);
      
      if (catScores.length > 0) {
        const avg = catScores.reduce((a, b) => a + b, 0) / catScores.length;
        const weighted = (avg / 5) * cat.weight;
        totalWeighted += weighted;
        weightSum += cat.weight;
      }
    });

    // Normalize to 100 if weights don't sum to 100
    const final = weightSum > 0 ? (totalWeighted / weightSum) * 100 : 0;
    return final.toFixed(1);
  };

  const getCatIcon = (name) => {
    const n = name.toLowerCase();
    if (n.includes("technical")) return <FiZap />;
    if (n.includes("development")) return <FiTrendingUp />;
    if (n.includes("communication")) return <FiMessageCircle />;
    if (n.includes("session")) return <FiCalendar />;
    if (n.includes("professionalism")) return <FiShield />;
    if (n.includes("engagement")) return <FiSmile />;
    if (n.includes("safety")) return <FiAlertTriangle />;
    if (n.includes("satisfaction")) return <FiHeart />;
    if (n.includes("compliance")) return <FiFileText />;
    return <FiTarget />;
  };

  const handleSubmit = async () => {
    if (!selectedCoach || !selectedStudent) {
      alert("Please select both a coach and a student.");
      return;
    }

    const { rated, total } = getCompletionStats();
    if (rated < total) {
      alert(`Feedback Incomplete: Please provide ratings for all ${total} quality indicators before submitting. You have ${total - rated} remaining.`);
      return;
    }

    setSubmitting(true);
    try {
      const user = JSON.parse(localStorage.getItem("sixers_user") || "{}");
      const payload = {
        coach_id: parseInt(selectedCoach),
        student_id: parseInt(selectedStudent),
        evaluator_id: user.user_id || user.parent_id || user.id || 0,
        evaluator_role: user.role || "Parent",
        comments,
        recommendation,
        scores: Object.entries(scores).map(([mid, s]) => ({ metric_id: parseInt(mid), score: s }))
      };

      const res = await fetch(`${API_BASE}/evaluations/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        alert(data.detail || "Submission failed.");
      }
    } catch (e) {
      alert("Error submitting evaluation.");
    } finally {
      setSubmitting(false);
    }
  };

  const getCompletionStats = () => {
    const allMetrics = categories.flatMap(c => c.metrics);
    const rated = allMetrics.filter(m => scores[m.id]).length;
    const total = allMetrics.length;
    const pct = total > 0 ? (rated / total) * 100 : 0;
    return { rated, total, pct };
  };

  if (submitted) {
    return (
      <div style={{ ...gc, padding: 60, textAlign: "center", maxWidth: 600, margin: "60px auto", borderRadius: 32 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
          <FiCheckCircle size={48} color="#10b981" />
        </div>
        <h2 style={{ color: T.blue, fontSize: 24, fontWeight: 900 }}>Evaluation Submitted!</h2>
        <p style={{ color: "rgba(3,78,123,0.6)", marginBottom: 30, fontSize: 15 }}>
          Your feedback has been successfully recorded. Thank you for helping our coaches improve.
        </p>
        <button onClick={() => setSubmitted(false)} style={{
          padding: "14px 32px", borderRadius: 12, border: "none", background: T.blue, color: "#fff", fontWeight: 800, cursor: "pointer", boxShadow: `0 8px 20px ${T.blue}33`
        }}>Submit Another</button>
      </div>
    );
  }

  const { rated, total, pct } = getCompletionStats();

  return (
    <div style={{ minHeight: "100dvh", background: T.pageBg, padding: "20px 20px", fontFamily: "'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", color: T.textPrimary }}>
        {/* Unified 1-Line Header Action Bar */}
        <div style={{ ...gc, position: "sticky", top: 10, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 20, background: T.cardBg, border: `1.5px solid ${T.blue}15`, borderRadius: 16, padding: "12px 20px", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}>
          
          {/* 1. Title & Icon (Left) */}
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <PageHeaderIcon icon={<FaClipboardCheck size={20} color={getBrandColors().blueDark} />} />
            <div style={{ minWidth: 260 }}>
              <h2 style={{ fontSize: "20px", fontWeight: 900, color: T.textPrimary, margin: 0, letterSpacing: "-0.01em" }}>
                Performance Evaluation
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontSize: "11px", color: T.textMuted, marginTop: "2px", fontWeight: 600 }}>
                <span>Coaching Standard Benchmark</span>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.yellow }} />
                <span>{total} Quality Indicators</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", flex: 1, justifyContent: "flex-end" }}>
            {/* 2. Coach Select */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
              <label style={{ fontSize: 10, fontWeight: 900, color: T.textMuted, textTransform: "uppercase" }}>Primary Coach</label>
              <select value={selectedCoach} onChange={e => setSelectedCoach(e.target.value)}
                style={{ width: "100%", background: "transparent", borderTop: "none", borderRight: "none", borderLeft: "none", color: T.textPrimary, outline: "none", fontSize: 13, fontWeight: 800, padding: 0, borderBottom: isDark ? `2px solid rgba(255,255,255,0.1)` : `2px solid ${T.blue}22`, paddingBottom: 4, cursor: "pointer" }}>
                <option value="">Select Coach</option>
                {coaches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* 3. Student Select */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
              <label style={{ fontSize: 10, fontWeight: 900, color: T.textMuted, textTransform: "uppercase" }}>Evaluating Student</label>
              {students.length === 1 ? (
                <div style={{ width: "100%", color: T.textPrimary, fontSize: 13, fontWeight: 800, padding: 0, borderBottom: isDark ? `2px solid rgba(255,255,255,0.1)` : `2px solid ${T.blue}22`, paddingBottom: 4 }}>
                  {students[0].name}
                </div>
              ) : (
                <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}
                  style={{ width: "100%", background: "transparent", borderTop: "none", borderRight: "none", borderLeft: "none", color: T.textPrimary, outline: "none", fontSize: 13, fontWeight: 800, padding: 0, borderBottom: isDark ? `2px solid rgba(255,255,255,0.1)` : `2px solid ${T.blue}22`, paddingBottom: 4, cursor: "pointer" }}>
                  <option value="">Select Student</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </div>

            {/* 4. Completion Progress */}
            <div style={{ minWidth: 260, borderLeft: `1px solid ${T.divider}`, paddingLeft: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FiTarget size={14} color={T.textPrimary} />
                  <span style={{ fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.05em", color: T.textPrimary }}>Completion</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 900, color: T.textPrimary }}>{rated} / {total} Rated</span>
              </div>
              <div style={{ height: 6, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.08)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: T.yellow, transition: "0.4s cubic-bezier(0.4, 0, 0.2, 1)", borderRadius: 4 }} />
              </div>
            </div>
          </div>
        </div>

      <div style={{ ...gc, marginBottom: 20, background: T.cardBg, border: `1.5px solid ${T.blue}15`, borderRadius: 16, overflow: "hidden", boxShadow: T.glassSh }}>
        {categories.map((cat, idx) => (
          <div key={cat.id} style={{ borderBottom: idx !== categories.length - 1 ? `3px solid ${T.divider}` : "none" }}>
            {/* Category Header */}
            <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: isDark ? "rgba(255,255,255,0.02)" : "rgba(3,78,123,0.01)", borderBottom: `1px solid ${T.divider}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.yellow, color: T.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900 }}>
                  {getCatIcon(cat.name)}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: T.textPrimary }}>{cat.name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 800, textTransform: "uppercase", marginTop: 2 }}>{cat.weight}% weighting</div>
                </div>
              </div>
            </div>

            {/* Category Metrics Form Body */}
            <div style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {cat.metrics.map((m, mIdx) => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: mIdx !== cat.metrics.length - 1 ? `1px solid ${T.divider}` : "none", paddingBottom: mIdx !== cat.metrics.length - 1 ? 12 : 0 }}>
                    <div style={{ color: T.textPrimary, fontSize: 14, fontWeight: 600, width: "65%", lineHeight: 1.4 }}>{m.name}</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[1, 2, 3, 4, 5].map(star => (
                        <div key={star} 
                             onClick={() => handleScoreChange(m.id, star)}
                              style={{
                               width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                               cursor: "pointer", transition: "0.2s",
                               background: scores[m.id] === star ? T.yellow : (isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.05)"),
                               color: scores[m.id] === star ? T.blue : T.textMuted,
                               fontWeight: scores[m.id] === star ? 900 : 700,
                               fontSize: 13,
                               border: scores[m.id] === star ? `1.5px solid ${T.yellow}` : (isDark ? "0px solid rgba(255,255,255,0.1)" : "0px solid transparent")
                             }}>
                          {star}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...gc, background: T.cardBg, border: `1.5px solid ${T.blue}15`, borderRadius: 16, padding: 20, marginBottom: 20, boxShadow: T.glassSh }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <FiEdit3 color={T.textPrimary} size={16}/>
          <label style={{ fontSize: 12, fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", letterSpacing: "0.05em" }}>Qualitative Feedback</label>
        </div>
        <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Type any specific observations or areas for improvement..."
          style={{ width: "100%", minHeight: 100, border: `1.5px solid ${T.divider}`, borderRadius: 12, padding: "12px 16px", color: T.textPrimary, outline: "none", resize: "vertical", fontSize: 14, fontWeight: 500, background: T.inputBg }} />
      </div>

      <button disabled={submitting} onClick={handleSubmit} style={{
        width: "100%", marginTop: 4, padding: "14px", borderRadius: 12, border: "none", marginBottom: 30,
        background: `linear-gradient(135deg, ${T.yellow} 0%, #d9b60a 100%)`, 
        color: "#fff", fontSize: 15, fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        boxShadow: `0 8px 24px ${T.yellow}33`, transition: "0.2s", opacity: submitting ? 0.7 : 1
      }}>
        {submitting ? "Submitting..." : <><FiSend /> Submit Full Evaluation</>}
      </button>
      </div>
    </div>
  );
};

export default CoachEvaluation;
